import { useCallback, useState } from "react"
import { v4 as uuid } from "uuid"

/**
 * $TOKEN admin helpers.
 *
 * 🔴 NOTHING HERE CALLS `Number()` ON A MONEY VALUE, and nothing added here may.
 * $TOKEN carries 18 decimal places, the presale price 8, and USD arrives as
 * BigInt cents. A JS number holds ~15-17 significant digits, so a single
 * `Number(amount)` silently rounds a figure the operator is about to compare
 * against the database or edit and submit back. All formatting below is string
 * surgery for that reason.
 */

/**
 * `t`, narrowed to return a string.
 *
 * i18next types `t` as returning `TFunctionResult` — `string | object | array` —
 * the moment interpolation options are passed, which every JSX child and toast
 * payload then rejects. This panel renders plain strings and nothing else, so
 * the narrowing lives here instead of an `as string` at forty call sites.
 *
 * Use it as `useTranslation("translation", { useSuspense: false }) as { t: Translate }`.
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string

/** Thousands separators on the integer part, decimals left exactly as sent. */
export const formatDecimalString = (raw: string | undefined, maxDecimals = 6): string => {
  if (raw === undefined || raw === null || raw === "") return "—"
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return raw

  const negative = raw.startsWith("-")
  const unsigned = negative ? raw.slice(1) : raw
  const [intPart, decPart = ""] = unsigned.split(".")

  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  // Truncated, never rounded — rounding a displayed token amount up would show a
  // balance the player does not have.
  const trimmed = decPart.slice(0, maxDecimals).replace(/0+$/, "")

  return `${negative ? "-" : ""}${grouped}${trimmed ? `.${trimmed}` : ""}`
}

/**
 * Integer cents → a display dollar string.
 *
 * Pure string surgery — no `cents / 100`, and no BigInt either. Totals raised
 * across a presale run go past the safe-integer range, so division would round a
 * revenue figure; BigInt would work but the admin panel targets below ES2020, so
 * literals are unavailable and the runtime dependency is not worth taking for
 * what is a decimal-point insertion.
 */
export const formatUsdCents = (raw: string | undefined): string => {
  if (!raw || !/^-?\d+$/.test(raw)) return "—"

  const negative = raw.startsWith("-")
  const digits = (negative ? raw.slice(1) : raw).padStart(3, "0")

  const dollars = digits.slice(0, -2).replace(/^0+(?=\d)/, "")
  const cents = digits.slice(-2)

  const grouped = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  return `${negative ? "-" : ""}$${grouped}.${cents}`
}

/**
 * Compare two non-negative integer STRINGS.
 *
 * Needed because these are cent values that can exceed Number.MAX_SAFE_INTEGER,
 * where `Number(a) > Number(b)` starts comparing rounded values. Longer wins;
 * equal length falls back to lexicographic, which is correct for digit strings
 * of the same length.
 *
 * Returns -1, 0 or 1. Non-numeric input returns 0 so callers treat it as
 * "cannot compare" and let their own format validation report the problem.
 */
export const compareIntegerStrings = (a: string, b: string): number => {
  if (!isIntegerString(a) || !isIntegerString(b)) return 0

  const x = a.trim().replace(/^0+(?=\d)/, "")
  const y = b.trim().replace(/^0+(?=\d)/, "")

  if (x.length !== y.length) return x.length > y.length ? 1 : -1
  return x === y ? 0 : x > y ? 1 : -1
}

/**
 * Compare two NON-NEGATIVE decimal strings, without `Number()`.
 *
 * `compareIntegerStrings` cannot be used for $TOKEN: the values carry up to 18
 * decimal places, and `Number("1000000.000000000000000001")` is exactly
 * `1000000`, so a float comparison would call an over-withdrawal legal.
 *
 * Fractions are right-padded to the same width before comparing, because "0.9"
 * is larger than "0.10" but sorts smaller as raw text.
 *
 * Returns -1, 0 or 1. Non-numeric or negative input returns 0 so callers treat
 * it as "cannot compare" and let their own format validation report it.
 */
export const compareDecimalStrings = (a: string, b: string): number => {
  if (!isDecimalString(a) || !isDecimalString(b)) return 0

  const [ai, af = ""] = a.trim().split(".")
  const [bi, bf = ""] = b.trim().split(".")

  const x = ai.replace(/^0+(?=\d)/, "")
  const y = bi.replace(/^0+(?=\d)/, "")
  if (x.length !== y.length) return x.length > y.length ? 1 : -1
  if (x !== y) return x > y ? 1 : -1

  const width = Math.max(af.length, bf.length)
  const fx = af.padEnd(width, "0")
  const fy = bf.padEnd(width, "0")
  return fx === fy ? 0 : fx > fy ? 1 : -1
}

/** Basis points → a percentage label. bps are small integers, so this is safe. */
export const formatBps = (bps: number): string => `${(bps / 100).toFixed(2)}%`

export const formatDateTime = (iso: string | undefined): string => {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/** Decimal string, optionally signed. Used to validate before submitting. */
export const isDecimalString = (raw: string, { allowNegative = false } = {}): boolean =>
  new RegExp(`^${allowNegative ? "-?" : ""}\\d+(\\.\\d+)?$`).test(raw.trim())

/** Non-negative integer string, for cent fields. */
export const isIntegerString = (raw: string): boolean => /^\d+$/.test(raw.trim())

export const isPositiveDecimal = (raw: string): boolean => isDecimalString(raw) && /[1-9]/.test(raw)

/**
 * A stable idempotency key for one operator INTENT.
 *
 * ⚠️ THE POINT IS THAT IT DOES NOT CHANGE BETWEEN RETRIES. The server dedupes on
 * this key, so a double-clicked "Adjust balance" or a resubmit after a timeout
 * must carry the SAME value or it books twice. Generating a fresh key per submit
 * would make the whole mechanism decorative.
 *
 * `reset()` is called only when the operator starts a genuinely new operation —
 * i.e. when a modal is opened, not when it is submitted.
 */
export const useIdempotencyKey = (): { key: string; reset: () => void } => {
  const [key, setKey] = useState<string>(() => uuid())
  const reset = useCallback(() => setKey(uuid()), [])
  return { key, reset }
}

/**
 * Failure code → translation key.
 *
 * A generic "something went wrong" on these hides the one fact that explains the
 * refusal — most sharply for PURCHASE_FINAL, where the answer is a deliberate
 * business rule and not a fault to escalate.
 *
 * Keys, not copy: the strings live in the `token` namespace so an operator
 * reading the panel in Russian is not dropped into English at the exact moment
 * something is refused.
 */
export const TOKEN_ERROR_KEYS: Record<string, string> = {
  PURCHASE_FINAL: "token.errorPurchaseFinal",
  PURCHASE_NOT_FOUND: "token.errorPurchaseNotFound",
  POSITION_NOT_FOUND: "token.errorPositionNotFound",
  ALREADY_CLAIMED: "token.errorAlreadyClaimed",
  INSUFFICIENT_TOKEN: "token.errorInsufficientToken",
  REASON_REQUIRED: "token.errorReasonRequired",
  AMOUNT_OUT_OF_RANGE: "token.errorAmountOutOfRange",
  CONFIG_MISSING: "token.errorConfigMissing",
  TOKEN_DISABLED: "token.errorTokenDisabled",
  INTERNAL_ERROR: "token.errorInternal"
}

/**
 * Operator copy for a failure code, localized.
 *
 * ⚠️ Accepts BOTH the bare code and the `TOKEN_`-prefixed form the API actually
 * sends. The table above is keyed on the bare name (`INSUFFICIENT_TOKEN`) while
 * core-api returns `TOKEN_INSUFFICIENT_TOKEN`, so a plain lookup matches only
 * `TOKEN_DISABLED` — whose bare name happens to start with the prefix — and
 * every other code falls through to the raw backend message. That message is
 * written for a PLAYER ("You do not have enough unstaked $TOKEN"), which is the
 * wrong voice entirely for an admin adjusting somebody else's balance.
 *
 * `fallback` is the server's message: shown only for codes with no copy of
 * their own, which is better than a blank refusal.
 */
export const tokenErrorCopy = (t: Translate, code?: string, fallback?: string): string => {
  if (!code) return fallback || t("token.errorGeneric")
  const bare = code.startsWith("TOKEN_") ? code.slice("TOKEN_".length) : code
  const key = TOKEN_ERROR_KEYS[code] || TOKEN_ERROR_KEYS[bare]
  return key ? t(key) : fallback || t("token.errorGeneric")
}

export type TokenTabType = "presale" | "pools" | "analytics" | "manual-ops" | "bulk-grant"

/** Tab id → translation key. The strip renders `t(labelKey)`. */
export const TOKEN_TABS: Array<{ id: TokenTabType; labelKey: string }> = [
  { id: "presale", labelKey: "token.tabPresale" },
  { id: "pools", labelKey: "token.tabPools" },
  { id: "analytics", labelKey: "token.tabAnalytics" },
  { id: "manual-ops", labelKey: "token.tabManualOps" },
  { id: "bulk-grant", labelKey: "token.tabBulkGrant" }
]
